'use client';

import React from 'react';
import { createVeriffFrame } from '@veriff/incontext-sdk';
import { useInitVeriffKYC } from '@/backend/user/get-init-verification-kyc';
import { useUser, useUserStoreActions } from '@/store';
import { useGetUser } from '@/backend/user/get-user/get-user';
import ds from '@/styles/ds/ds.module.scss';

export const statusTxt = {
	0: 'PENDING',
	1: 'APPROVED',
	2: 'REJECTED',
	4: 'RE-REQUESTED',
};

export const VERIFF_STATUS = {
	EXPIRED: 'expired',
	PENDING: 'pending',
	RESUBMISSION_REQUESTED: 'resubmission_requested',
	ABANDONED: 'abandoned',
	REQUESTED: 'requested',
	APPROVED: 'approved',
	DECLINED: 'declined',
};

const VerificationLevel_2 = () => {
	const { initVeriffKYC } = useInitVeriffKYC();
	const { getUser } = useGetUser();
	const user = useUser();

	// veriffApplicantId: null,;

	const getVerificationSuccess = (url) => {
		if (url) {
			const veriffFrame = createVeriffFrame({
				url: url,
				onEvent(msg) {
					switch (msg) {
						case 'CANCELED':
							closeVeriffFrame();
							getUser({});
							break;
						case 'FINISHED':
							getUser({});
							closeVeriffFrame();
							break;
						default:
							break;
					}
				},
			});

			const closeVeriffFrame = () => veriffFrame?.close();
			return () => closeVeriffFrame();
		}
	};

	let buttonText: string | null;
	switch (user?.veriffStatus) {
		case VERIFF_STATUS.PENDING:
		case VERIFF_STATUS.ABANDONED:
		case VERIFF_STATUS.EXPIRED:
			buttonText = 'Continue';
			break;
		case VERIFF_STATUS.APPROVED:
			buttonText = 'Approved';
			break;
		case VERIFF_STATUS.RESUBMISSION_REQUESTED:
		case VERIFF_STATUS.DECLINED:
			buttonText = 'Resubmit';
			break;
		case VERIFF_STATUS.REQUESTED:
			buttonText = 'Submitted';
			break;
		default:
			buttonText = 'Submit';
	}

	const Content = () => {
		switch (user?.veriffStatus) {
			case VERIFF_STATUS.PENDING:
			case VERIFF_STATUS.ABANDONED:
			case VERIFF_STATUS.EXPIRED:
			case VERIFF_STATUS.RESUBMISSION_REQUESTED:
				return (
					<div className="flex flex-col gap-1.5">
						<p className={`${ds.section_header_primary}`}>
							Let&apos;s get you verified
						</p>
						<p>
							Before you start, prepare your identity document and make sure it
							is valid.
						</p>
					</div>
				);
			case VERIFF_STATUS.REQUESTED:
				return (
					<div className="flex flex-col gap-1.5">
						<p className={`${ds.section_header_primary}`}>
							Your documents are under review
						</p>
						<p>
							Your identity documents are currently under review by our team.
							We&apos;ll let you know over email once your submission has been
							processed. We appreciate your patience.
						</p>
					</div>
				);
			case VERIFF_STATUS.DECLINED:
				return (
					<div className="flex flex-col gap-1.5">
						<p className={`${ds.section_header_primary}`}>
							Your verification submission was declined
						</p>
						<p>
							There were one or more issues with your submitted documents.
							Please submit for verification again or contact us at{' '}
							<a
								href="mailto:support@otherworld.xyz"
								target="_blank"
								className="underline"
							>
								support@otherworld.xyz
							</a>
							.
						</p>
					</div>
				);
			default:
				return (
					<div className="flex flex-col gap-1.5">
						<p className={`${ds.section_header_primary}`}>
							Let&apos;s get you verified
						</p>
						<p>
							Before you start, prepare your identity document and make sure it
							is valid.
						</p>
					</div>
				);
		}
	};

	return (
		<section className="flex flex-col gap-5 ">
			{user?.veriffStatus && <Content />}
			<button
				aria-label="Submit Verification"
				className={`${ds.primary} ${ds.purpleWhite} h-10 md:max-w-[150px] capitalize`}
				disabled={
					user?.veriffStatus === VERIFF_STATUS.APPROVED ||
					user?.veriffStatus === VERIFF_STATUS.REQUESTED
				}
				onClick={async () => {
					try {
						const data = await initVeriffKYC();

						if (data.success) {
							const url = data?.verification?.url;

							if (url) {
								getVerificationSuccess(url);
							}
						}
					} catch {}
				}}
			>
				{user?.veriffStatus && <>{buttonText}</>}
			</button>
		</section>
	);
};

export default VerificationLevel_2;
